<script setup lang="ts">
import type {PropType} from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  value: {
    type: Object,
    default: Array as PropType<Array<string>>,
  },
  currentDateRange: {
    type: Date,
    default: new Date(),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
  },
  errorMessage: {
    type: String,
    default: 'error',
  },
  minDate: {
    type: Date,
    default: new Date(),
  },
  maxDate: {
    type: Date,
    default: new Date('01/10/2030'),
  },
});

const emit = defineEmits(['date-selected', 'current-date-range-update']);

const model = computed({
  get() {
    return props.value;
  },
  set(value) {
    emit('date-selected', value);
  },
});

const datePicker = ref();
const currentMonth = ref(props.currentDateRange);

function nextMonth() {
  updateMonth(1);
}

function previousMonth() {
  updateMonth(-1);
}

function updateMonth(direction: number) {
  const newDate = new Date(new Date(currentMonth.value).setMonth(currentMonth.value.getMonth() + direction));
  currentMonth.value = newDate;

  datePicker.value.setMonthYear({
    month: currentMonth.value.getMonth(),
    year: currentMonth.value.getFullYear(),
  });

  emit('current-date-range-update', newDate);
}

defineExpose({
  nextMonth,
  previousMonth,
});
</script>

<template>
  <Datepicker ref="datePicker" v-model="model" placeholder="Select date" :enable-time-picker="false" multi-dates multi-dates-limit="3" :month-change-on-scroll="false" :min-date="minDate" :max-date="maxDate" :min="minDate" :start-date="currentMonth" :config="{modeHeight: 200}" month-name-format="long" no-today inline auto-apply :disabled disable-month-year-select />
  <FormsFieldError v-show="error">{{ errorMessage }}</FormsFieldError>
</template>

<style lang="scss">
@use '@/assets/scss/queries';

.dp__calendar_row,
.dp__calendar_header_item,
.dp__cell_inner {
  margin: 0;
  padding: 0 !important;
}

.dp__flex_display {
  justify-content: center;
}

.dp__menu {
  border-radius: 10px;
  border-width: 0;
  overflow: hidden;
}

.dp__month_year_select {
  color: black;
  font-size: 1.6rem;
  font-weight: 400;

  @include queries.for-size(phone-only) {
    font-size: 2.5rem;
  }
}

.dp__month_year_row {
  background-color: #f4f4f4;
}

.dp__cell_inner {
  font-size: 12px;
}

.dp__active_date {
  background-color: var(--ever-color);
  border-radius: 50%;
}

.dp__date_hover {
  border-radius: 50%;
}

.dp__today {
  border-color: gray;
  border-radius: 50%;
}

.dp__calendar_header_item {
  font-size: 11px;
  font-weight: 600;
  padding-top: 1rem;
}

.dp__calendar_header_separator {
  display: none;
}

.dp__inner_nav {
  svg {
    fill: black;
    stroke: black;
  }
}

.dp__overlay_cell_active {
  background-color: var(--ever-color);
}
</style>
